import React, { useState } from 'react';
import {
  Box,
  Brand,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Icon,
  IconButton,
  Icons,
  Logos,
  Stack,
  Typography,
} from '@nextiva/next-ui-components';
import { CampaignDialogV3Props } from 'interface/WizardV3';
import {
  BoxStep,
  CampaignActions,
  CampaignTitle,
  FaqTypography,
  SavedCheckIcon,
  SavedStack,
  SavedTypography,
  StyledLink,
} from './CampaignDialogV3.styled';
import { MESSAGING_PLAN_FIELDS, STEPSLIST } from './constants';
import { SubmitConfirmation } from './SubmitConfirmation';
import { CustomerConsent } from './CustomerConsent/CustomerConsent';
import { CustomerPrompts } from './CustomerPrompts/CustomerPrompts';
import { MessagingPlan } from './MessagingPlan/MessagingPlan';
import { MessageContent } from './MessageContent/MessageContent';
import { PrivacyPolicy } from './PrivacyPolicy/PrivacyPolicy';
import StepsItems from './StepsItems';
import { CompanyType } from './CompanyType/CompanyType';
import { useWizardContextV3 } from './WizardContextV3/WizardContextV3';
import { BrandRegistration } from './BrandRegistration/BrandRegistration';
import { CancelRegistrationDialog } from './MessagingPlan/RegistrationDialog/RegistrationDialog';

const dataTestId = 'PlatformAdminUI-Messaging-CampaignDialog';
const CampaignDialogV3 = ({
  open,
  handleCloseDialog,
}: CampaignDialogV3Props) => {
  const {
    formState,
    handleNextStep,
    handleBackStep,
    isVisitedStep,
    doSubmit,
    doCampaignRefetch,
    state,
    resetField,
    setPrevSubUseCases,
    prevSubUseCases,
  } = useWizardContextV3();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const saveStatus = isVisitedStep(state.currentStep) ? 'saved' : 'no-saved';
  const noErrors = Object.keys(formState.errors).length === 0;
  const isReadyToSubmit = state.isLastStep && formState.isValid && noErrors;
  const onDoneClick = state.isLastStep ? doSubmit : handleNextStep;
  const disableNext =
    state.loading || (state.isLastStep ? !isReadyToSubmit : !formState.isValid);

  const onClose = () => {
    doCampaignRefetch();
    handleCloseDialog();
  };
  return (
    <Dialog
      fullScreen
      open={open}
      scroll="paper"
      onClose={onClose}
      data-testid={`${dataTestId}`}
    >
      <CampaignTitle>
        <Brand
          logo={Logos.brandIcon}
          data-testid={`${dataTestId}-Title-NextivaLogo`}
          size={32}
        />
        <Stack direction="row" spacing={3} alignItems="center">
          {!state.allStepsCompleted && (
            <>
              <SavedStack
                direction="row"
                spacing={1}
                alignItems="center"
                data-status={saveStatus}
              >
                <SavedCheckIcon
                  icon={Icons.checkCircleSolid}
                  data-status={saveStatus}
                />
                <SavedTypography
                  variant="subtitle3"
                  data-testid={`${dataTestId}-Title-Saved`}
                  data-status={saveStatus}
                >
                  Saved
                </SavedTypography>
              </SavedStack>
            </>
          )}
          <IconButton
            aria-label="close-integrations"
            onClick={onClose}
            data-testid={`${dataTestId}-Title-CloseButton`}
          >
            <Icon icon={Icons.close} size={16} />
          </IconButton>
        </Stack>
      </CampaignTitle>

      {state.allStepsCompleted && (
        <DialogContent
          id="scroll-dialog-description"
          tabIndex={-1}
          data-testid={`${dataTestId}-Content`}
          sx={{ padding: '32px 64px' }}
        >
          <SubmitConfirmation onDoneClick={onClose} />
        </DialogContent>
      )}

      {!state.allStepsCompleted && state.currentStep === 0 && (
        <DialogContent
          id="scroll-dialog-description"
          tabIndex={-1}
          data-testid={`${dataTestId}-Content`}
          sx={{ padding: '32px 64px' }}
        >
          <CompanyType />
        </DialogContent>
      )}

      {!state.allStepsCompleted && state.currentStep > 0 && (
        <Stack
          direction="row"
          spacing={0}
          alignItems="center"
          divider={<Divider orientation="vertical" flexItem />}
          sx={{ borderBottom: '1px solid rgb(237, 239, 244)' }}
        >
          <Typography
            variant="h6"
            sx={{
              whiteSpace: 'nowrap',
              width: '20.25rem',
              padding: '24px 48px',
              display: 'flex',
            }}
          >
            Messaging registration
          </Typography>

          <Typography
            variant="h6"
            sx={{
              padding: '24px 64px',
            }}
          >
            {STEPSLIST[state.currentStep - 1].name}
          </Typography>
        </Stack>
      )}
      {!state.allStepsCompleted && state.currentStep > 0 && (
        <Box display="flex" overflow="auto" height="100%">
          <Box
            sx={{
              width: '20.25rem',
              borderRight: '1px solid rgb(237, 239, 244)',
              boxShadow: 'rgba(22, 22, 24, 0.16) 0px 0.25rem 0.5rem',
            }}
          >
            <BoxStep>
              {STEPSLIST.map((list) => (
                <StepsItems
                  key={list.id}
                  fields={list.fields}
                  title={list.name}
                  isSelected={state.currentStep === list.id}
                  value={list.id}
                />
              ))}
            </BoxStep>
          </Box>

          <DialogContent
            id="scroll-dialog-description"
            tabIndex={-1}
            data-testid={`${dataTestId}-Content`}
            sx={{ padding: '32px 64px' }}
          >
            {state.currentStep === 1 && <BrandRegistration />}
            {state.currentStep === 2 && <CustomerConsent />}
            {state.currentStep === 3 && <MessagingPlan />}
            {state.currentStep === 4 && <CustomerPrompts />}
            {state.currentStep === 5 && <MessageContent />}
            {state.currentStep === 6 && <PrivacyPolicy />}
          </DialogContent>
        </Box>
      )}

      <Divider sx={{ margin: '0 0px 16px 156px' }} />
      {!state.allStepsCompleted && (
        <CampaignActions>
          <Stack direction="row" spacing={0} alignItems="center">
            <Box
              sx={{
                height: 49,
                width: 49,
                borderRadius: '50%',
                overflow: 'hidden',
              }}
            >
              <Brand
                data-testid={`${dataTestId}-Actions-BrandLogo`}
                logo={Logos.brandIconAvatar}
                sx={{ height: 49, width: 49 }}
              />
            </Box>
            <Box sx={{ marginLeft: '13px', width: '203px' }}>
              <FaqTypography variant="caption">
                {'Visit our '}
                <StyledLink
                  href="https://www.nextiva.com/support/articles/sms-faqs.html"
                  underline="none"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${dataTestId}-Actions-FAQLink`}
                >
                  <Typography variant="caption" fontWeight="bold">
                    FAQ page
                  </Typography>
                </StyledLink>
                {' for more information about the text campaign registry. '}
              </FaqTypography>
            </Box>
            {state.currentStep > 1 && (
              <Button
                variant="contained"
                color="secondary"
                data-testid={`${dataTestId}-Previous-Button`}
                onClick={handleBackStep}
                sx={{
                  '& .MuiButton-sizeSmall': {
                    padding: '12px 16px',
                    ml: '64px',
                  },
                }}
              >
                Previous
              </Button>
            )}
          </Stack>
          <Stack direction="row" spacing={4}>
            {prevSubUseCases !== null && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => setOpenDialog(true)}
                data-testid={`${dataTestId}-Actions-CancelButton`}
                disabled={state.loading}
                sx={{
                  '& .MuiButton-sizeSmall': {
                    padding: '12px 16px',
                  },
                }}
              >
                Cancel new registration
              </Button>
            )}
            {state.currentStep > 0 && (
              <Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onDoneClick}
                  data-testid={`${dataTestId}-Actions-Button`}
                  disabled={disableNext}
                  sx={{
                    '& .MuiButton-sizeSmall': {
                      padding: '12px 24px',
                    },
                  }}
                >
                  {state.isLastStep ? 'Submit' : 'Next'}
                </Button>
              </Box>
            )}
          </Stack>
          <CancelRegistrationDialog
            open={openDialog}
            handleButton1={() => setOpenDialog(false)}
            handleButton2={() => {
              resetField(MESSAGING_PLAN_FIELDS.SUB_USE_CASES, {
                defaultValue: prevSubUseCases,
              });
              setOpenDialog(false);
              setPrevSubUseCases(null);
            }}
          />
        </CampaignActions>
      )}
    </Dialog>
  );
};

export default CampaignDialogV3;
